import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { Notification } from '../lib/notifications-api';

interface SseNotificationMessage {
  id: string;
  category: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  createdAt: string;
}

interface SseCountMessage {
  unreadCount: number;
}

interface SseReadMessage {
  notificationId: string;
  readAt: string;
}

/**
 * Hook for SSE real-time notification updates
 */
export function useNotificationStream() {
  const { accessToken, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create new EventSource with auth token in query param
    // Note: SSE doesn't support custom headers, so we use a different approach
    // The backend should validate the JWT from the Authorization header via cookie or query
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const url = `${apiUrl}/notifications-v2/stream?token=${encodeURIComponent(accessToken)}`;
    
    const eventSource = new EventSource(url, {
      withCredentials: true,
    });

    eventSourceRef.current = eventSource;

    eventSource.addEventListener('notification', (event) => {
      const notification: SseNotificationMessage = JSON.parse(event.data);
      
      // Add new notification to the cache
      queryClient.setQueryData<{ pages: { data: Notification[] }[] }>(
        ['notifications'],
        (old) => {
          if (!old) return old;
          
          const newNotification: Notification = {
            notificationId: notification.id,
            userId: '', // Will be filled by the backend
            category: notification.category,
            title: notification.title,
            body: notification.body,
            data: notification.data,
            isRead: false,
            readAt: null,
            createdAt: notification.createdAt,
          };

          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  data: [newNotification, ...page.data],
                };
              }
              return page;
            }),
          };
        },
      );

      // Invalidate unread count to refetch
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    });

    eventSource.addEventListener('count', (event) => {
      const { unreadCount }: SseCountMessage = JSON.parse(event.data);
      queryClient.setQueryData(['notifications', 'unread-count'], { count: unreadCount });
    });

    eventSource.addEventListener('read', (event) => {
      const { notificationId, readAt }: SseReadMessage = JSON.parse(event.data);
      
      // Update notification in cache
      queryClient.setQueryData<{ pages: { data: Notification[] }[] }>(
        ['notifications'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) =>
                n.notificationId === notificationId
                  ? { ...n, isRead: true, readAt }
                  : n,
              ),
            })),
          };
        },
      );
    });

    eventSource.addEventListener('ping', () => {
      // Heartbeat received, connection is alive
      reconnectAttempts.current = 0;
    });

    eventSource.onerror = () => {
      eventSource.close();
      eventSourceRef.current = null;

      // Attempt to reconnect with exponential backoff
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current++;
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
    };
  }, [isAuthenticated, accessToken, queryClient]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return {
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
    reconnect: connect,
  };
}
